import React from 'react';
import { Lightbulb, Palette, Heart, Brain, TrendingUp, Target, Zap, Eye } from 'lucide-react';
import { ColorPalette, hslToHex } from '../utils/colorGenerator';

interface ColorInsightsProps {
  palette: ColorPalette;
}

export const ColorInsights: React.FC<ColorInsightsProps> = ({ palette }) => {
  // Extract HSL values from color strings
  const getHSLValues = (hslString: string) => {
    const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!match) return { h: 0, s: 0, l: 0 };
    return {
      h: parseInt(match[1]),
      s: parseInt(match[2]),
      l: parseInt(match[3])
    };
  };

  // Analyze color temperature
  const analyzeTemperature = () => {
    const hues = palette.colors.map(color => getHSLValues(color).h);
    const warmHues = hues.filter(h => (h >= 0 && h <= 60) || (h >= 300 && h <= 360));
    const coolHues = hues.filter(h => h >= 180 && h <= 240);
    
    if (warmHues.length > coolHues.length) {
      return { type: 'warm', description: 'Creates feelings of energy, passion, and comfort' };
    } else if (coolHues.length > warmHues.length) {
      return { type: 'cool', description: 'Evokes calmness, trust, and professionalism' };
    } else {
      return { type: 'balanced', description: 'Offers versatility and broad appeal' };
    }
  };

  // Analyze saturation levels
  const analyzeSaturation = () => {
    const saturations = palette.colors.map(color => getHSLValues(color).s);
    const avgSaturation = saturations.reduce((sum, s) => sum + s, 0) / saturations.length;
    
    if (avgSaturation > 70) {
      return { level: 'high', description: 'Bold and vibrant, great for attention-grabbing designs' };
    } else if (avgSaturation > 40) {
      return { level: 'medium', description: 'Balanced vibrancy, suitable for most applications' };
    } else {
      return { level: 'low', description: 'Subtle and sophisticated, ideal for minimalist designs' };
    }
  };

  // Analyze lightness distribution
  const analyzeLightness = () => {
    const lightnesses = palette.colors.map(color => getHSLValues(color).l);
    const avgLightness = lightnesses.reduce((sum, l) => sum + l, 0) / lightnesses.length;
    const range = Math.max(...lightnesses) - Math.min(...lightnesses);
    
    return {
      average: avgLightness,
      range: range,
      contrast: range > 60 ? 'high' : range > 30 ? 'medium' : 'low'
    };
  };

  // Get color psychology insights
  const getColorPsychology = () => {
    const dominantHue = palette.colors
      .map(color => getHSLValues(color))
      .sort((a, b) => b.s - a.s)[0]; // Get most saturated color as dominant

    const hue = dominantHue.h;
    
    if (hue >= 0 && hue < 30) {
      return { color: 'Red', traits: ['Energy', 'Passion', 'Urgency', 'Power'], industries: ['Food', 'Entertainment', 'Sports'] };
    } else if (hue >= 30 && hue < 60) {
      return { color: 'Orange/Yellow', traits: ['Optimism', 'Creativity', 'Warmth', 'Attention'], industries: ['Education', 'Children', 'Creative'] };
    } else if (hue >= 60 && hue < 150) {
      return { color: 'Green', traits: ['Nature', 'Growth', 'Harmony', 'Health'], industries: ['Environment', 'Health', 'Finance'] };
    } else if (hue >= 150 && hue < 210) {
      return { color: 'Cyan', traits: ['Freshness', 'Technology', 'Innovation', 'Clarity'], industries: ['Technology', 'Healthcare', 'Water'] };
    } else if (hue >= 210 && hue < 270) {
      return { color: 'Blue', traits: ['Trust', 'Stability', 'Professionalism', 'Calm'], industries: ['Corporate', 'Finance', 'Healthcare'] };
    } else {
      return { color: 'Purple/Magenta', traits: ['Luxury', 'Creativity', 'Mystery', 'Sophistication'], industries: ['Beauty', 'Luxury', 'Creative'] };
    }
  };

  // Generate design recommendations
  const getDesignRecommendations = () => {
    const temperature = analyzeTemperature();
    const saturation = analyzeSaturation();
    const lightness = analyzeLightness();
    
    const recommendations = [];
    
    if (temperature.type === 'warm') {
      recommendations.push('Perfect for call-to-action buttons and highlighting important elements');
    } else if (temperature.type === 'cool') {
      recommendations.push('Excellent for backgrounds and creating a sense of space');
    }
    
    if (saturation.level === 'high') {
      recommendations.push('Use sparingly as accents to avoid overwhelming users');
    } else if (saturation.level === 'low') {
      recommendations.push('Great for text and subtle UI elements');
    }
    
    if (lightness.contrast === 'high') {
      recommendations.push('Excellent contrast for accessibility and readability');
    } else {
      recommendations.push('Consider adding more contrast for better accessibility');
    }
    
    return recommendations;
  };

  const temperature = analyzeTemperature();
  const saturation = analyzeSaturation();
  const lightness = analyzeLightness();
  const psychology = getColorPsychology();
  const recommendations = getDesignRecommendations();

  return (
    <div className="space-y-8">
      {/* Color Analysis Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
              <Zap className="text-white" size={16} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Temperature</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                temperature.type === 'warm' ? 'bg-red-100 text-red-700' :
                temperature.type === 'cool' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {temperature.type.charAt(0).toUpperCase() + temperature.type.slice(1)}
              </span>
            </div>
            <p className="text-sm text-gray-600">{temperature.description}</p>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <Palette className="text-white" size={16} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Saturation</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                saturation.level === 'high' ? 'bg-purple-100 text-purple-700' :
                saturation.level === 'medium' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {saturation.level.charAt(0).toUpperCase() + saturation.level.slice(1)}
              </span>
            </div>
            <p className="text-sm text-gray-600">{saturation.description}</p>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Eye className="text-white" size={16} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Contrast</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                lightness.contrast === 'high' ? 'bg-green-100 text-green-700' :
                lightness.contrast === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {lightness.contrast.charAt(0).toUpperCase() + lightness.contrast.slice(1)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Range: {lightness.range.toFixed(0)}% • Avg: {lightness.average.toFixed(0)}%
            </p>
          </div>
        </div>
      </div>

      {/* Color Psychology */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
            <Brain className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Color Psychology</h3>
            <p className="text-sm text-gray-600">Emotional and psychological impact of your palette</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Dominant Color: {psychology.color}</h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Psychological Traits:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {psychology.traits.map((trait, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm"
                    >
                      {trait}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Recommended Industries</h4>
            <div className="space-y-2">
              {psychology.industries.map((industry, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Target className="text-blue-500" size={14} />
                  <span className="text-sm text-gray-700">{industry}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Design Recommendations */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
            <Lightbulb className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Design Recommendations</h3>
            <p className="text-sm text-gray-600">Best practices for using this palette effectively</p>
          </div>
        </div>

        <div className="space-y-4">
          {recommendations.map((recommendation, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
              <TrendingUp className="text-green-600 mt-0.5" size={16} />
              <p className="text-sm text-green-800">{recommendation}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Color Harmony Analysis */}
      <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
            <Heart className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Harmony Analysis</h3>
            <p className="text-sm text-gray-600">How well your colors work together</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Color Distribution</h4>
            <div className="space-y-3">
              {palette.colors.map((color, index) => {
                const hsl = getHSLValues(color);
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <div
                      className="w-8 h-8 rounded-lg border border-gray-200"
                      style={{ backgroundColor: hslToHex(color) }}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">{palette.names[index]}</div>
                      <div className="text-xs text-gray-600">
                        H:{hsl.h}° S:{hsl.s}% L:{hsl.l}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Usage Guidelines</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <span className="font-medium text-blue-600">60%</span>
                <span>Use the lightest color as your primary background</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-medium text-blue-600">30%</span>
                <span>Apply the medium tones for secondary elements</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-medium text-blue-600">10%</span>
                <span>Reserve the darkest colors for accents and emphasis</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};